import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { api, money } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

function TiltFrame({ p }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[520px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease }}
        className="relative aspect-[4/5] overflow-hidden bg-[var(--terra)]"
        data-testid="hero-product-frame"
      >
        <motion.img
          src={p?.image || "/img/sneakers.jpg"}
          alt={p?.name || "Featured product"}
          style={{ y: imgY, scale: 1.2 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_35%,rgba(25,20,35,0.5))]" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8, ease }}
        className="absolute -left-2 bottom-10 rounded-2xl bg-[var(--bone)] p-4 shadow-[0_20px_60px_-20px_rgba(20,18,16,0.45)] sm:-left-10"
        style={{ transform: "translateZ(60px)" }}
      >
        <p className="eyebrow !text-[10px] text-[var(--ink-soft)]">Most wanted · {p?.category || "Trending"}</p>
        <p className="font-display mt-1 max-w-[240px] truncate text-xl">{p?.name || "Aero Runner"}</p>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-sm font-semibold">{p ? money(p.price) : "$139"}</span>
          <Link to={p ? `/product/${p.slug}` : "/shop"} className="flex items-center gap-1 text-xs font-semibold text-[var(--terra-deep)]" data-testid="hero-feature-link">View <ArrowUpRight size={14} /></Link>
        </div>
      </motion.div>
      <div className="absolute -right-3 -top-4 h-28 w-28 sm:-right-8 sm:h-32 sm:w-32" aria-hidden="true">
        <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <circle cx="50" cy="50" r="49" fill="#191423" />
          <text fontSize="9.5" fontFamily="JetBrains Mono" letterSpacing="2.4" fill="#f4efe8"><textPath href="#circ">MOST WANTED · MELIC · EDIT · </textPath></text>
          <circle cx="50" cy="50" r="7" fill="#e8a93d" />
        </svg>
      </div>
    </div>
  );
}

export function Hero() {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : [];
        const best = items.slice().sort((a, b) => (b.price || 0) - (a.price || 0))[0];
        if (best) setP(best);
      })
      .catch(() => {});
  }, []);
  return (
    <section className="grain relative overflow-hidden" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 pb-10 pt-10 md:px-10 lg:grid-cols-12 lg:pb-6 lg:pt-16">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow flex items-center gap-3 text-[var(--ink-soft)]">
            <span className="inline-block h-2 w-2 rounded-full bg-[var(--terra)]" /> The most-wanted edit — now in stock
          </motion.p>
          <MaskLines
            testid="hero-headline"
            className="font-display mt-6 text-5xl leading-[0.98] tracking-tight sm:text-6xl lg:text-[5.6rem]"
            lines={[
              { text: "Everything" },
              { text: "everyone wants,", className: "italic text-[var(--terra)]" },
              { text: "in one place." },
            ]}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease }} className="mt-8 max-w-md text-base leading-relaxed text-[var(--ink-soft)] md:text-lg">
            The jackets, gadgets and gear topping every marketplace chart — hand-picked, priced right and shipped fast.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/shop" className="group flex items-center gap-3 rounded-full bg-[var(--ink)] py-2 pl-7 pr-2 text-sm font-semibold text-[var(--bone)] transition-colors hover:bg-[var(--terra)]" data-testid="hero-shop-now-button">
              Shop the edit
              <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--bone)] text-[var(--ink)] transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
            </Link>
            <a href="#categories" className="link-u text-sm font-semibold" data-testid="hero-categories-link">Browse categories</a>
          </motion.div>
        </div>
        <div className="lg:col-span-5"><TiltFrame p={p} /></div>
      </div>
      <div className="overflow-hidden px-3" aria-hidden="true">
        <div className="flex justify-between font-display text-[22vw] font-semibold leading-[0.8] tracking-tighter lg:text-[19vw]">
          {"MELIC".split("").map((c, i) => (
            <motion.span key={i} className={i === 4 ? "italic text-[var(--terra)]" : "outline-word"} initial={{ y: "100%" }} animate={{ y: "8%" }} transition={{ duration: 1.2, delay: 0.5 + i * 0.07, ease }}>
              {c}
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}
